import { buildUri, CLOUDFRONT_URI, DEFAULT_LOCALE_CODE } from '../../const';
import { isRelativePath, isJsonString } from '../../updated/utils';
import { LearningObjectUtil } from './LearningObjectUtil';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';

function setIfNotUndefined( object, path, value ) {
    if ( value !== undefined ) {
        set( object, path, value );
    }
}

export class CourseUtil {

    /**
     * @typedef CourseInputGraphQL
     * @property { string } id - The ID of the course.
     * @property { boolean } isPublic - Whether or not the course is public.
     * @property { boolean } isSequential - Whether or not the course is sequential.
     * @property { { locale: string, name: string, description: string }[] } locales - The locale data.
     * @property { string[] } learningObjectIds - The list of learning object IDs to include in the course.
     * @property { string[] } tags - The tags associated with the course.
     * @property { string } thumbnailImageId - The ID of the thumbnail image.
     * @property { string } heroImageId - The ID of the hero image.
     * @property { string } bannerImageId - The ID of the banner image.
     * @property { object } metadata - The metadata.
     */

    /**
     * @typedef CourseInputLocal  
     * ...
     */

    /**
     * @typedef { CourseInputGraphQL & CourseInputLocal } CourseInput
     */

    /**
     * Builds the GraphQL course input given the response of an already existing
     * course.
     * 
     * @param { object } courseData - The data to read.
     * @returns { CourseInputGraphQL }
     */
    static buildPatchInputFromCourse( courseData ) {
        const dataCopy = cloneDeep( courseData );
        return {
            id: this.getId( dataCopy ),
            isPublic: this.getIsPublic( dataCopy ),
            isSequential: this.getIsSequential( dataCopy ),
            locales: this.getCmxLocales( dataCopy ),
            learningObjectIds: this.getLearningObjectIds( dataCopy ),
            tags: this.getTags( dataCopy ),
            thumbnailImageId: this.getThumbnailImageId( dataCopy ),
            heroImageId: this.getHeroImageId( dataCopy ),
            bannerImageId: this.getBannerImageId( dataCopy ),
            metadata: this.getMetadata( dataCopy ),
        };
    }

    /**
     * Transforms provided input into an input structure supported by GQL.
     * 
     * @param { object } previousData - The previous course data to modify.
     * @param { CourseInput } input - The data to store.
     * @returns { object }
     */
    static transformPatchInput( previousData, input ) {
        const newInput = this.buildPatchInputFromCourse( previousData );

        // Copy over the basic properties that don't need any special handling.
        setIfNotUndefined( newInput, 'id', input?.id );
        setIfNotUndefined( newInput, 'isPublic', input?.isPublic );
        setIfNotUndefined( newInput, 'isSequential', input?.isSequential );
        setIfNotUndefined( newInput, 'learningObjectIds', input?.learningObjectIds );
        setIfNotUndefined( newInput, 'tags', input?.tags );
        setIfNotUndefined( newInput, 'thumbnailImageId', input?.thumbnailImageId );
        setIfNotUndefined( newInput, 'heroImageId', input?.heroImageId );
        setIfNotUndefined( newInput, 'bannerImageId', input?.bannerImageId );
        setIfNotUndefined( newInput, 'metadata', input?.metadata );
        setIfNotUndefined( newInput, 'locales', input?.locales );
        setIfNotUndefined( newInput, 'points', input?.points );
        setIfNotUndefined( newInput, 'isFeatured', input?.isFeatured );
        setIfNotUndefined( newInput, 'weight', input?.weight );

        return newInput;
    }

    /**
     * Retrieves the ID from the provided course data.
     * 
     * @param { object } courseData - The data to read.
     * @returns { string }
     */
    static getId( courseData ) {
        return courseData?.node?.id;
    }

    /**
     * Retrieves the name from a course object in the language represented by the
     * provided locale code. If no locale code is provided, a default locale code
     * of `en-US` will be used.
     * 
     * @param { object } courseData - The data to read.
     * @param { string } localeCode - The language to retrieve the data in.
     * @returns { string }
     */
    static getName( courseData, localeCode = DEFAULT_LOCALE_CODE ) {
        // Validate the locale code parameter.
        if ( !localeCode ) {
            localeCode = DEFAULT_LOCALE_CODE;
        }

        const locales = courseData?.node?.locales ? courseData.node.locales : [];
        const translatedName = locales.find( ( data ) => ( data?.locale == localeCode ) )?.name;
        return translatedName;
    }

    /**
     * Retrieves the description from a course object in the language represented
     * by the provided locale code. If no locale code is provided, a default locale
     * code of `en-US` will be used.
     * 
     * @param { object } courseData - The data to read.
     * @param { string } localeCode - The language to retrieve the data in.
     * @returns { string }
     */
    static getDescription( courseData, localeCode = DEFAULT_LOCALE_CODE ) {
        // Validate the locale code parameter.
        if ( !localeCode ) {
            localeCode = DEFAULT_LOCALE_CODE;
        }

        const locales = courseData?.node?.locales ? courseData.node.locales : [];
        const translatedDescription = locales.find( ( data ) => ( data?.locale == localeCode ) )?.description;
        return translatedDescription;
    }

    /**
     * Retrieves the list of all locale data for the provided course. This includes
     * locales and translations stored in CMX as well as any additional languages
     * potentially stored in the course's metadata.
     * 
     * Once CMX supports all language/locale data that we need, we should be able to
     * get by only using the CMX locale data and not the metadata, but it technically
     * doesn't hurt to leave the support for the metadata translations.
     * 
     * @param { object } courseData - The data to read.
     * @returns { { locale: string, name: string, description: string }[] }
     */
    static getLocales( courseData ) { 
        return [ ...( courseData?.node?.locales ?? [] ), ...( courseData?.node?.metadata?.nonCMXLocales ?? [] ) ]
                .map( loc => ( { locale: loc.locale, name: loc.name, description: loc.description } ) );
    }

    /**
     * Retrieves the list of locale data for the provided course. These are only the
     * locales that are officially supported by CMX and are stored in CMX.
     * 
     * @param { object[] } courseData - The data to read.
     * @returns { { locale: string, name: string, description: string }[] }
     */
    static getCmxLocales( courseData ) {
        return courseData?.node?.locales?.map( ( localeData ) => ( { 
            locale: localeData?.locale,
            name: localeData?.name,
            description: localeData?.description
        } ) );
    }

    /**
     * Returns whether or not the provided course is considered to be active.
     * 
     * @param { object } courseData - The data to read.
     * @returns { boolean }
     */
    static getIsActive( courseData ) {
        return Boolean( courseData?.node?.isActive );
    }

    /**
     * Returns whether or not the provided course is considered to be public.
     * 
     * @param { object } courseData - The data to read.
     * @returns { boolean }
     */
    static getIsPublic( courseData ) {
        return Boolean( courseData?.node?.isPublic );
    }

    /**
     * Returns whether or not the course objects are required to be in sequential
     * order.
     * 
     * @param { object } courseData - The data to read.
     * @returns { boolean }
     */
    static getIsSequential( courseData ) {
        return Boolean( courseData?.node?.isSequential );
    }

    /**
     * Retrieves the date the course was created in milleseconds.
     * 
     * @param { object } courseData - The data to read.
     * @returns { number }
     */
    static getDateOfCreation( courseData ) {
        const time = courseData?.node?.createdAt;
        if ( time !== undefined ) {
            // Convert Unix date stamp from seconds to milliseconds.
            return ( time * 1000 );
        } else {
            return undefined;
        }
    }

    /**
     * Retrieves the date the course was last updated in milleseconds.
     * 
     * @param { object } courseData - The data to read.
     * @returns { number }
     */
    static getDateOfLastUpdate( courseData ) {
        const time = courseData?.node?.updatedAt;
        if ( time !== undefined ) {
            // Convert Unix date stamp from seconds to milliseconds.
            return ( time * 1000 );
        } else {
            return undefined;
        }
    }

    /**
     * Retrieves the tags from the course. Note that course tags are not currently
     * translatable.
     * 
     * @param { object } courseData - The data to read.
     * @param { object } options - Options that change what data is retrieved.
     * @returns { string[] }
     */
    static getTags( courseData, { hideJsonTags = true } = {} ) {
        let tags = courseData?.node?.tags;

        // Remove all JSON string tags unless requested otherwise.
        if ( hideJsonTags && tags && Array.isArray( tags ) ) {
            tags = tags.filter( ( tagValue ) => !isJsonString( tagValue ) );
        }

        return tags;
    }

    /**
     * Retrieves the tagIds from the course 
     * 
     * @param { object } courseData - The data to read.
      * @returns { string[] }
     */
    static getTagIds( courseData  ) {
        const tags = courseData?.node?.tagIds;

        return tags;
    }

    /**
     * Retrieves the average rating from the course.
     * 
     * @param { object } courseData - The data to read.
     * @returns { number }
     */
    static getRatingAverage( courseData ) {
        return courseData?.node?.ratingAverage;
    }

    /**
     * Retrieves the number of times the course has been rated by users.
     * 
     * @param { object } courseData - The data to read.
     * @returns { number }
     */
    static getRatingCount( courseData ) {
        return courseData?.node?.ratingCount;
    }

    /**
     * Retrieves the thumbnail image for the course.
     * 
     * @param { object } courseData - The data to read.
     * @returns { object }
     */
    static getThumbnailImage( courseData ) {
        return courseData?.node?.thumbnailImage;
    }

    /**
     * Retrieves the thumbnail image ID for the course.
     * 
     * @param { object } courseData - The data to read.
     * @returns { object }
     */
    static getThumbnailImageId( courseData ) {
        return this.getThumbnailImage( courseData )?.id;
    }

    /**
     * Retrieves the URL of the thumbnail image for the course.
     * 
     * @param { object } courseData - The data to read.
     * @param { { forceAbsolutePath: boolean } } config - Settings to change how the URL is returned.
     * @returns { string }
     */
    static getThumbnailImageUrl( courseData, { forceAbsolutePath = true } = {} ) {
        const path = this.getThumbnailImage( courseData )?.url;
        if ( path ) {
            if ( forceAbsolutePath && isRelativePath( path ) ) {
                return buildUri( [ CLOUDFRONT_URI, path ] );
            } else {
                return path;
            }
        }
        return undefined;
    }

    /**
     * Retrieves the hero image for the course.
     * 
     * @param { object } courseData - The data to read.
     * @returns { object }
     */
    static getHeroImage( courseData ) {
        return courseData?.node?.heroImage;
    }

    /**
     * Retrieves the hero image ID for the course.
     * 
     * @param { object } courseData - The data to read.
     * @returns { object }
     */
    static getHeroImageId( courseData ) {
        return this.getHeroImage( courseData )?.id;
    }

    /**
     * Retrieves the URL of the hero image for the course.
     * 
     * @param { object } courseData - The data to read.
     * @param { { forceAbsolutePath: boolean } } config - Settings to change how the URL is returned.
     * @returns { string }
     */
    static getHeroImageUrl( courseData, { forceAbsolutePath = true } = {} ) {
        const path = this.getHeroImage( courseData )?.url;
        if ( path ) {
            if ( forceAbsolutePath && isRelativePath( path ) ) {
                return buildUri( [ CLOUDFRONT_URI, path ] );
            } else {
                return path;
            }
        }
        return undefined;
    }

    /**
     * Retrieves the banner image for the course.
     * 
     * @param { object } courseData - The data to read.
     * @returns { object }
     */
    static getBannerImage( courseData ) {
        return courseData?.node?.heroImage;
    }

    /**
     * Retrieves the banner image ID for the course.
     * 
     * @param { object } courseData - The data to read.
     * @returns { object }
     */
    static getBannerImageId( courseData ) {
        return this.getBannerImage( courseData )?.id;
    }

    /**
     * Retrieves the URL of the banner image for the course.
     * 
     * @param { object } courseData - The data to read.
     * @param { { forceAbsolutePath: boolean } } config - Settings to change how the URL is returned.
     * @returns { string }
     */
    static getBannerImageUrl( courseData, { forceAbsolutePath = true } = {} ) {
        const path = this.getBannerImage( courseData )?.url;
        if ( path ) {
            if ( forceAbsolutePath && isRelativePath( path ) ) {
                return buildUri( [ CLOUDFRONT_URI, path ] );
            } else {
                return path;
            }
        }
        return undefined;
    }

    /**
     * Retrieves the number of learning objects that exist inside the course.
     * 
     * @param { object } courseData - The data to read.
     * @returns { number }
     */
    static getLearningObjectCount( courseData ) {
        let learningObjectCount = 0;

        // Attempt to rely on the `totalCount` property to get the total number of learning
        // objects in the course, but if that is not available, attempt to get the total
        // number using the actual learning object data array if possible.
        const totalCount = courseData?.node?.learningObjects?.totalCount;
        if ( totalCount !== undefined ) {
            learningObjectCount = totalCount;
        } else {
            const edgesLength = courseData?.node?.learningObjects?.edges?.length;
            if ( edgesLength !== undefined ) {
                learningObjectCount = edgesLength;
            }
        }

        return learningObjectCount;
    }

    /**
     * Retrieves the IDs of the learning objects that exist inside the course.
     * 
     * @param { object } courseData - The data to read.
     * @returns { string[] }
     */
    static getLearningObjectIds( courseData ) {
        const objectEdges = courseData?.node?.learningObjects?.edges || [];
        return objectEdges.map( ( learningObjectData ) => LearningObjectUtil.getId( learningObjectData ) );
    }

    /**
     * Retrieves the metadata from inside of the provided course.
     * 
     * @param { object } courseData - The data to read.
     * @returns { object }
     */
    static getMetadata( courseData ) {
        return courseData?.node?.metadata;
    }

    /**
     * Retrieves the company relationships from inside of the provided course.
     * 
     * @param { object } courseData - The data to read.
     * @returns { string[] } = company relationship UUIDs
     */
    static getCompanyRelationshipIds( courseData ) {
        return courseData?.node?.companyRelationshipIds;
    }

    /**
     * Retrieves the product from inside of the provided course.
     * 
     * @param { object } courseData - The data to read.
     * @returns { string[] } = company relationship UUIDs
     */
    static getProductIds( courseData ) {
        return courseData?.node?.productIds;
    }

    /**
     * Retrieves the brand IDs that the course is a part of.
     * 
     * @param { object } courseData - The data to read.
     * @returns { string[] }
     */
    static getBrandIds( courseData ) {
        return courseData?.node?.brandIds;
    }

    /**
     * Retrieves the segment IDs that the course is a part of.
     * 
     * @param { object } courseData - The data to read.
     * @returns { string[] }
     */
    static getSegmentIds( courseData ) {
        return courseData?.node?.segmentIds ?? [];
    }

    /**
     * Retrieves the locale IDs that the course is a part of.
     * 
     * @param { object } courseData - The data to read.
     * @returns { string[] }
     */
    static getLocaleIds( courseData ) {
        return courseData?.node?.localeIds ?? [];
    }

    /**
     * Retrieves the category IDs that the course is a part of.
     * 
     * @param { object } courseData - The data to read.
     * @returns { string[] }
     */
    static getCategoryIds( courseData ) {
        return courseData?.node?.categoryIds;
    }
    
    /**
     * Retrieves the enrollmentCount .
     * 
     * @param { object } enrollmentCount - The data to read.
     * @returns { number }
     */
    static getEnrollmentCount( courseData ) {
        return courseData?.node?.enrollmentCount;
    }

    static getIsFeatured( courseData  ) {
        return courseData?.node?.isFeatured === true || false;
    }

    static getWeight( courseData  ) {
        return courseData?.node?.weight || 0;
    }
}
